// src/Logic/Functions/Hooks/useContactInquiries.ts
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  IContactMessagesResponse,
  IInquiryProps,
  UseContactInquiriesOptions,
  InquiryFilter,
} from "@model/contactus.model";

export function useContactInquiries({
  initialPage = 1,
  initialPerPage = 10,
  perPageOptions: perPageInput = [5, 10, 50, 100],
}: UseContactInquiriesOptions = {}) {
  // data + meta
  const [inquiries, setInquiries] = useState<IInquiryProps[]>([]);
  const [totalCount, setTotalCount] = useState<number>();
  const [resolvedTotalCount, setResolvedTotalCount] = useState<number>();
  const [unresolvedTotalCount, setUnresolvedTotalCount] = useState<number>();

  // pagination + search + filter
  const [page, setPage] = useState(initialPage);
  const [perPage, setPerPage] = useState(initialPerPage);
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState<InquiryFilter>("all");
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement | null>(null);

  // card expansion
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // per-page dropdown
  const perPageOptions = useMemo(() => {
    const s = new Set<number>(perPageInput);
    s.add(initialPerPage);
    return Array.from(s).sort((a, b) => a - b);
  }, [perPageInput, initialPerPage]);

  const [perPageDropdownOpen, setPerPageDropdownOpen] = useState(false);
  const [perPageDropdownActiveIndex, setPerPageDropdownActiveIndex] =
    useState<number>(() =>
      Math.max(
        0,
        perPageOptions.findIndex((o) => o === perPage)
      )
    );
  const perPageDropdownWrapRef = useRef<HTMLDivElement>(null);
  const perPageDropdownBtnRef = useRef<HTMLButtonElement>(null);
  const perPageDropdownListRef = useRef<HTMLUListElement>(null);

  // keep active index in sync with current perPage
  useEffect(() => {
    setPerPageDropdownActiveIndex(
      Math.max(
        0,
        perPageOptions.findIndex((o) => o === perPage)
      )
    );
  }, [perPage, perPageOptions]);

  // close per-page dropdown on outside click
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (perPageDropdownWrapRef.current?.contains(e.target as Node)) return;
      setPerPageDropdownOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // close status menu on outside click
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (filterRef.current?.contains(e.target as Node)) return;
      setFilterOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // client-side text filter
  const filteredInquiries = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return inquiries;
    return inquiries.filter((it) => {
      const name = (it.name ?? "").toLowerCase();
      const email = (it.email ?? "").toLowerCase();
      const phone = (it.phone ?? "").toLowerCase();
      const msg = (it.message ?? "").toLowerCase();
      return (
        name.includes(q) ||
        email.includes(q) ||
        phone.includes(q) ||
        msg.includes(q)
      );
    });
  }, [inquiries, searchQuery]);

  // global counts from backend (preferred)
  const allCount = (resolvedTotalCount ?? 0) + (unresolvedTotalCount ?? 0);

  // total pages based on global count
  const totalPages = useMemo(
    () => (perPage ? Math.max(1, Math.ceil(allCount / perPage)) : null),
    [allCount, perPage]
  );

  const requestParams = useMemo(() => ({ page, perPage }), [page, perPage]);

  // apply one server response
  const applyServerResponse = useCallback(
    (payload: {
      items?: IInquiryProps[];
      meta?: IContactMessagesResponse["meta"];
    }) => {
      if (payload.items) setInquiries(payload.items);
      const m = payload.meta;
      if (!m) return;
      if (typeof m.total === "number") setTotalCount(m.total);
      if (typeof m.page === "number") setPage(m.page);
      if (typeof m.perPage === "number") setPerPage(m.perPage);
      if (typeof m.resolvedTotal === "number")
        setResolvedTotalCount(m.resolvedTotal);
      if (typeof m.unresolvedTotal === "number")
        setUnresolvedTotalCount(m.unresolvedTotal);
    },
    []
  );

  // per-page dropdown interactions
  const togglePerPageDropdown = useCallback(() => {
    setPerPageDropdownOpen((o) => !o);
  }, []);

  const onPerPageDropdownButtonKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setPerPageDropdownOpen(true);
        setTimeout(() => perPageDropdownListRef.current?.focus(), 0);
      }
    },
    []
  );

  const onPerPageDropdownListKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLUListElement>) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setPerPageDropdownOpen(false);
        perPageDropdownBtnRef.current?.focus();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setPerPageDropdownActiveIndex((i) =>
          Math.min(perPageOptions.length - 1, i + 1)
        );
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setPerPageDropdownActiveIndex((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === "Home") {
        e.preventDefault();
        setPerPageDropdownActiveIndex(0);
        return;
      }
      if (e.key === "End") {
        e.preventDefault();
        setPerPageDropdownActiveIndex(perPageOptions.length - 1);
        return;
      }
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        const value = perPageOptions[perPageDropdownActiveIndex];
        if (typeof value === "number") {
          setPage(1);
          setPerPage(value);
        }
        setPerPageDropdownOpen(false);
        perPageDropdownBtnRef.current?.focus();
      }
    },
    [perPageOptions, perPageDropdownActiveIndex]
  );

  const selectPerPageAt = useCallback(
    (idx: number) => {
      const value = perPageOptions[idx];
      if (typeof value === "number") {
        setPage(1);
        setPerPage(value);
      }
      setPerPageDropdownOpen(false);
      perPageDropdownBtnRef.current?.focus();
    },
    [perPageOptions]
  );

  // filter helpers
  const chooseFilter = useCallback((f: InquiryFilter) => {
    setFilter(f);
    setPage(1);
    setFilterOpen(false);
    setExpanded({});
  }, []);

  // expand/collapse per card
  const toggleExpand = useCallback((id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  // date label helper
  const formatWhen = useCallback((iso?: string) => {
    if (!iso) return "";
    return new Date(iso).toLocaleString();
  }, []);

  return {
    inquiries,
    filteredInquiries,
    totalCount,
    resolvedTotalCount,
    unresolvedTotalCount,
    searchQuery,
    setSearchQuery,
    page,
    setPage,
    totalPages,
    perPage,
    perPageOptions,
    perPageDropdownOpen,
    perPageDropdownActiveIndex,
    perPageDropdownWrapRef,
    perPageDropdownBtnRef,
    perPageDropdownListRef,
    togglePerPageDropdown,
    onPerPageDropdownButtonKeyDown,
    onPerPageDropdownListKeyDown,
    selectPerPageAt,
    requestParams,
    applyServerResponse,
    filter,
    setFilter,
    filterOpen,
    setFilterOpen,
    filterRef,
    chooseFilter,
    expanded,
    toggleExpand,
    allCount,
    resCount: resolvedTotalCount ?? 0,
    unresCount: unresolvedTotalCount ?? 0,
    formatWhen,
  };
}
